import {
  BULLET_PATTERNS_HTML,
  ALL_ORDERED_LIST_PATTERNS,
} from '../bulletPatterns.js';

/**
 * Converts bullet-prefixed paragraphs to HTML unordered lists
 */
export class BulletListConverter {
  convertHyphenListsToHtml(container) {
    const paragraphs = Array.from(container.querySelectorAll('p'));
    let currentListItems = [];
    let currentList = null;

    const bulletPatterns = BULLET_PATTERNS_HTML;

    paragraphs.forEach((p) => {
      const textContent = p.textContent || '';
      const trimmedText = textContent.trim();

      const matchedPattern = bulletPatterns.find((pattern) =>
        pattern.regex.test(trimmedText),
      );

      if (matchedPattern) {
        const listItemText = trimmedText.substring(matchedPattern.length);

        if (!currentList) {
          currentList = document.createElement('ul');
          p.parentNode.insertBefore(currentList, p);
        }

        const li = document.createElement('li');
        li.textContent = listItemText;

        const spans = p.querySelectorAll('span, strong, em, b, i');
        if (spans.length > 0) {
          li.innerHTML = p.innerHTML.replace(matchedPattern.regex, '');
        }

        currentList.appendChild(li);
        currentListItems.push(p);
      } else {
        if (currentList) {
          currentListItems.forEach((item) => item.remove());
          currentListItems = [];
          currentList = null;
        }
      }
    });

    if (currentList && currentListItems.length > 0) {
      currentListItems.forEach((item) => item.remove());
    }
  }
}

/**
 * Converts numbered paragraphs to HTML ordered lists with support for nested lists
 * Creates nested <ol> as SIBLINGS (not children) to match Volto/Slate's structure
 */
export class NumberedListConverter {
  /**
   * Get indentation level from paragraph styles and numbering pattern
   */
  getIndentationLevel(p, text) {
    const computedStyle = window.getComputedStyle(p);
    const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const textIndent = parseFloat(computedStyle.textIndent) || 0;

    // Check numbering depth (1. = level 1, 1.1. = level 2, 1.1.1. = level 3)
    const numberingMatch = text.match(/^(\d+\.)+/);
    if (numberingMatch) {
      const dots = (numberingMatch[0].match(/\./g) || []).length;
      return dots;
    }

    // Fallback to margin/padding detection
    const totalIndent = marginLeft + paddingLeft + Math.abs(textIndent);

    if (totalIndent < 25) return 1;
    if (totalIndent < 45) return 2;
    if (totalIndent < 65) return 3;
    if (totalIndent < 85) return 4;
    return 5;
  }

  /**
   * Extract number from text
   */
  extractNumber(text, pattern) {
    const match = text.match(pattern.regex);
    if (!match) return null;

    if (pattern.nested) {
      const allNumbers = text.match(/\d+/g);
      if (allNumbers && allNumbers.length > 1) {
        return parseInt(allNumbers[allNumbers.length - 1], 10);
      }
    }

    const numberMatch = text.match(/^(\d+)/);
    return numberMatch ? parseInt(numberMatch[1], 10) : null;
  }

  /**
   * Clean Word conditional comments and list markup from HTML
   */
  cleanWordListMarkup(html) {
    return html
      .replace(/<!--\[if !supportLists\]-->[\s\S]*?<!--\[endif\]-->/gi, '')
      .replace(/<span[^>]*mso-list[^>]*>[\s\S]*?<\/span>/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  convertNumberedListsToHtml(container) {
    const paragraphs = Array.from(container.querySelectorAll('p'));
    const orderedPatterns = ALL_ORDERED_LIST_PATTERNS;

    // Stack to track nested lists as SIBLINGS
    const listStack = [];
    const processedParagraphs = [];

    paragraphs.forEach((p) => {
      const textContent = p.textContent || '';
      const trimmedText = textContent.trim();

      const matchedPattern = orderedPatterns.find((pattern) =>
        pattern.regex.test(trimmedText),
      );

      if (matchedPattern) {
        const currentNumber = this.extractNumber(trimmedText, matchedPattern);
        const indentLevel = this.getIndentationLevel(p, trimmedText);

        if (currentNumber === null) return;

        let listItemText = trimmedText.replace(matchedPattern.regex, '').trim();

        let targetList = null;
        let targetLevel = null;

        // Pop lists from stack that are deeper than current level
        while (
          listStack.length > 0 &&
          listStack[listStack.length - 1].level > indentLevel
        ) {
          listStack.pop();
        }

        // Check if we can continue an existing list at this level
        if (listStack.length > 0) {
          const parentLevel = listStack[listStack.length - 1];

          // If this is a new nested level (one level deeper than parent)
          if (indentLevel === parentLevel.level + 1 && currentNumber === 1) {
            // Create new nested list as SIBLING of last <li>
            const newList = document.createElement('ol');
            const lastLi = parentLevel.list.lastElementChild;
            if (lastLi && lastLi.tagName === 'LI') {
              // Insert AFTER the last <li>, not inside it
              lastLi.parentNode.insertBefore(newList, lastLi.nextSibling);
            } else {
              parentLevel.list.appendChild(newList);
            }
            targetList = newList;
            targetLevel = {
              list: newList,
              level: indentLevel,
              expectedNumber: 1,
            };
            listStack.push(targetLevel);
          }
          // If continuing at same level as parent
          else if (
            indentLevel === parentLevel.level &&
            currentNumber === parentLevel.expectedNumber
          ) {
            targetList = parentLevel.list;
            targetLevel = parentLevel;
          }
        }

        // If no suitable list found and this is a level 1 item starting at 1
        if (!targetList && indentLevel === 1 && currentNumber === 1) {
          targetList = document.createElement('ol');
          p.parentNode.insertBefore(targetList, p);
          targetLevel = { list: targetList, level: 1, expectedNumber: 1 };
          listStack.push(targetLevel);
        }

        // If continuing an existing level 1 list
        if (!targetList && indentLevel === 1 && listStack.length > 0) {
          const level1 = listStack.find((l) => l.level === 1);
          if (level1 && currentNumber === level1.expectedNumber) {
            targetList = level1.list;
            targetLevel = level1;
          }
        }

        // Create list item if we have a target list
        if (targetList && targetLevel) {
          const li = document.createElement('li');

          let cleanedHtml = p.innerHTML
            .replace(matchedPattern.regex, '')
            .trim();
          cleanedHtml = this.cleanWordListMarkup(cleanedHtml);

          const spans = p.querySelectorAll('span, strong, em, b, i');
          if (spans.length > 0 && cleanedHtml) {
            li.innerHTML = cleanedHtml;
          } else {
            li.textContent = listItemText;
          }

          targetList.appendChild(li);
          processedParagraphs.push(p);
          targetLevel.expectedNumber++;
        }
      } else {
        listStack.length = 0;
      }
    });

    processedParagraphs.forEach((p) => p.remove());
  }
}
